import chatMessageTextStyles from 'css/src/components/agent/ChatMessageText.module.scss';

import React from 'react';

import MarkdownWithDiff from '../components/MarkdownWithDiff';
import type { CustomRendererComponentProps, ToolDefinition, ToolExecutionContext } from '../types';

import { AddChartSceneTool } from './AddChartSceneTool';
import { AddVoiceoverSceneTool } from './AddVoiceoverSceneTool';
import { EditChartTool } from './EditChartTool';
import { resolveOverlayRangeOperation } from './overlay-placement';
import { computeSceneShortIds } from './scene-addressing';
import {
    computeSceneGroupShortIds,
    resolveSceneGroupById,
    resolveSceneGroupHandle,
} from './scene-group-addressing';
import { getInterleavedTranscript } from './transcript-tokens';
import { VideoStateTool } from './VideoStateTool';

import { getTemplateDefaultCutawaySceneDesign } from 'js/src/data/templates';
import { checkpointVideo } from 'js/src/data/video-data';
import { refreshFirstScenePreviewOperation } from 'js/src/data/video-planner';
import {
    applyGeneratedChartConfigOperation,
    getDefaultChartConfig,
    setupChartSceneOperation,
} from 'js/src/libs/chart-asset-add';
import { SUPPORTED_CHART_TYPES, type SupportedChartType } from 'js/src/libs/default-chart-configs';
import type { RootState } from 'js/src/libs/RootState';
import { saveRawLuminary } from 'js/src/luminary';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';
import { moveOtherOverlaysAndCreateOverlayOperation } from 'js/src/luminary/luminary-operations';

interface AddChartOverlayArgs {
    scene_group_id: string;
    words: string[];
    chart_type: SupportedChartType;
    data: string;
}

export class AddChartOverlayTool extends VideoStateTool<AddChartOverlayArgs> {
    public static readonly toolName = 'add_chart_overlay';

    public override customRenderer = AddChartOverlayResultRenderer;

    public override getToolDefinition(): ToolDefinition {
        return {
            type: 'function',
            function: {
                name: AddChartOverlayTool.toolName,
                description: `Add a chart as an overlay scene inside a voiceover/talking-head (voicedriven) scene group, covering a chosen span of transcript words (picked like ${AddVoiceoverSceneTool.toolName}). Use ${AddChartSceneTool.toolName} instead to add a chart as its own standalone scene. After adding, verify with a screenshot and fix a wrong label, value, or type with ${EditChartTool.toolName}.`,
                parameters: {
                    type: 'object',
                    properties: {
                        scene_group_id: {
                            type: 'string',
                            description:
                                'The stable scene_group_id of the voicedriven scene group to add the chart cutaway to (from the video state).',
                        },
                        words: {
                            type: 'array',
                            items: { type: 'string' },
                            description:
                                'Consecutive transcript tokens the chart cutaway should cover. Must appear sequentially in the transcript exactly as spelled, including any `<pause seconds="N"/>` token that falls between them, and must not overlap an existing overlay.',
                        },
                        chart_type: {
                            type: 'string',
                            description: 'The kind of chart to create.',
                            enum: [...SUPPORTED_CHART_TYPES],
                        },
                        data: {
                            type: 'string',
                            description:
                                'A description of what the chart should show, with any actual numbers/labels included inline (e.g. "coin-flip outcomes — Heads 6, Tails 4"). Include figures when you have them; otherwise describe the data and the generator supplies it.',
                        },
                    },
                    required: ['scene_group_id', 'words', 'chart_type', 'data'],
                },
            },
        };
    }

    public override getToolDisplayName(): string {
        return 'Add Chart Overlay';
    }

    protected override onEditApplied(args: AddChartOverlayArgs): void {
        // A cutaway on the first scene group renders over the first scene, changing the thumbnail.
        // Resolve defensively — this runs after a successful edit, so it must never throw.
        const resolved = resolveSceneGroupById(this.luminary, args.scene_group_id);
        if (!('error' in resolved) && resolved.index === 0) {
            this.dispatch(refreshFirstScenePreviewOperation());
        }
    }

    public get statusIcon(): string {
        return 'bar_chart';
    }

    public async execute(args: AddChartOverlayArgs, context: ToolExecutionContext) {
        const { words, chart_type, data } = args;
        const { parentActionGroup } = context;

        try {
            const { sceneGroup } = resolveSceneGroupHandle(this.luminary, args);
            const groupShortId =
                computeSceneGroupShortIds(this.luminary.sceneGroups.map((group) => group.id)).get(
                    sceneGroup.id,
                ) ?? sceneGroup.id;

            const transcript = sceneGroup.originalTranscript;
            const transcriptTokens = getInterleavedTranscript(this.getState(), sceneGroup);
            if (!transcript || transcriptTokens.length === 0) {
                return `Error: Scene group ${groupShortId} does not have a transcript. Only voiceover and talking-head scene groups can have chart cutaways. Use ${AddChartSceneTool.toolName} for a standalone chart scene.`;
            }

            const config = getDefaultChartConfig(chart_type, 0);
            if (!config) {
                return `Error: No default chart config for type ${chart_type}`;
            }

            const cutawaySceneDesignId = getTemplateDefaultCutawaySceneDesign(
                this.getState(),
                this.luminary.template.id,
            );
            if (!cutawaySceneDesignId) {
                return `Error: Template "${this.luminary.template.id}" has no default cutaway scene design, so a chart cutaway can't be created.`;
            }

            const {
                resolveOverlayRange,
                moveOtherOverlaysAndCreateOverlay,
                setupChartScene,
                applyGeneratedChartConfig,
            } = makeOperationsCallable(
                {
                    resolveOverlayRangeOperation,
                    moveOtherOverlaysAndCreateOverlayOperation,
                    setupChartSceneOperation,
                    applyGeneratedChartConfigOperation,
                },
                this.luminary,
                this.dispatch,
                this._getState as () => RootState,
            );

            const range = resolveOverlayRange(sceneGroup, words);
            if ('error' in range) {
                return `Error: ${range.error}`;
            }

            const sceneIdsBefore = this.snapshotSceneIds();

            const overlaySceneId = moveOtherOverlaysAndCreateOverlay(
                sceneGroup.id,
                range.startTimeMillis,
                range.endTimeMillis,
                cutawaySceneDesignId,
                false,
                parentActionGroup,
            );

            // Creating the overlay also splits the base scene underneath it, so the
            // remainder of the scene group is a new base scene that needs blurring too.
            this.blurScenesCreatedSince(sceneIdsBefore, [overlaySceneId]);

            const setup = setupChartScene(overlaySceneId, chart_type, config);
            if (!setup) {
                return `Error: Could not place a chart on the new cutaway in scene group ${groupShortId}`;
            }

            const { sceneId, assetId } = setup;

            const status = await applyGeneratedChartConfig({
                sceneId,
                assetId,
                chartType: chart_type,
                data,
                extractChartDataFromResult: true,
                parentActionGroup,
            });

            // setupChartScene doesn't checkpoint, and applyGeneratedChartConfig only checkpoints
            // on success — so checkpoint here when data generation didn't.
            if (status !== 'success') {
                this.dispatch(
                    checkpointVideo({
                        video_data: saveRawLuminary(this.luminary),
                        systemTriggered: false,
                        parentActionGroup,
                    }),
                );
            }

            const overlaySceneShortId =
                computeSceneShortIds(this.luminary.scenes.map((scene) => scene.id)).get(sceneId) ??
                sceneId;

            if (status === 'success') {
                return `Success: Added a ${chart_type} chart cutaway over "${words.join(' ')}" in scene group ${groupShortId} (overlay scene_id ${overlaySceneShortId}) and populated it with data.`;
            }
            return `Success: Added a ${chart_type} chart cutaway over "${words.join(' ')}" in scene group ${groupShortId} (overlay scene_id ${overlaySceneShortId}), but data generation failed so the chart shows placeholder data. Use ${EditChartTool.toolName} to set the data.`;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error adding chart overlay: ${errorMessage}`;
        }
    }
}

const AddChartOverlayResultRenderer: React.FC<CustomRendererComponentProps> = ({
    isLoading,
    result,
}) => {
    if (isLoading) {
        return null;
    }
    return (
        <div className={chatMessageTextStyles['chat-message-text']}>
            <MarkdownWithDiff>{result}</MarkdownWithDiff>
        </div>
    );
};
